// PageBuilder.tsx
import React, { useState, useMemo, useEffect, useCallback, useContext, useRef } from 'react';
import styled from 'styled-components';
// TypeScript may error on side-effect CSS imports if no declaration is present.
// Suppress the type error for this import.
// @ts-ignore
import 'bootstrap/dist/css/bootstrap.min.css';

import { Element, Section, CanvasItem, SettingsPanelConfig, TempSettings } from '../../types/types';
import Sidebar from './Sidebar';
import Header from './Header';
import Canvas from './Canvas';
import Footer from './Footer';
import SettingsPanel from './SettingsPanel';
import { Container, Title, TitleGroup, TitleWrapper } from '../listing/AMSListing';
import StyledText from '../StyledComponents/StyledText';
import { useTheme } from '../../middleware/ThemeContext';
import { Button } from '../form/Button';
import ViewIcon from '../../assets/icons/ViewIcon';
import FormsIcon from '../../assets/icons/FormsIcon';
import StyledToolTip from '../StyledComponents/StyledTooltip';
import FormDraftIcon from '../../assets/icons/FormDraftIcon';
import SingleColumnIcon from '../../assets/icons/SingleColumnIcon';
import MultiColumnIcon from '../../assets/icons/MultiColumnIcon';
import { useGetMenusQuery, useGetPageElementsQuery, useGetPageLayoutQuery, usePublishHeaderFooterMutation, usePublishPageLayoutMutation } from '../../api/pageBuilderApiSlice';
import { mapTemplateToBasicElements } from '../../utils/pageElementMapper';
import { getElementDefaultSettings } from '../../utils/pageElementDefaultSettings';
import { transformCanvasToPayload, generatePagePayload } from '../../utils/canvasPayloadTransformer';
import PreviewModal from './PreviewModal';
import { useNavigate, useParams, UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';
import { LoadingSpinner } from '../../features/ui/LoadingSpinner';
import { useToast } from '../toast/ToastContext';
import { font } from '../../utils/themes';
import { SkeletonCell } from '../SkeletonCell';
import { Status } from '../../utils/statusHelper';
import ConfirmationPrompt from '../StyledComponents/ConfirmationPrompt';
import NewCopyIcon from '../../assets/icons/NewCopyIcon';
import TickIcon from '../../assets/icons/TickIcon';
import { copyToClipboard } from '../../utils/helper';
import VerifyIcon from '../../assets/icons/VerifyIcon';
import RoundedGreenTick from '../../assets/icons/RoundedGreenTick';

const AppContainer = styled.div`
  display: flex;
  min-height: 100vh;
  background: #f8f9fa;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
`;

const getFontFamilyFromHeaderHtml = (headerHtml: string): string | null => {
    if (!headerHtml || !headerHtml.trim()) {
        return null;
    }

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(headerHtml, 'text/html');
        const links = Array.from(doc.querySelectorAll('link[rel="stylesheet"][href*="fonts.googleapis.com"]'));

        for (const link of links) {
            const href = link.getAttribute('href');
            if (!href) {
                continue;
            }

            try {
                const url = new URL(href, window.location.origin);
                const families = url.searchParams.getAll('family');
                if (families.length > 0) {
                    const family = families[0].split(':')[0].replace(/\+/g, ' ');
                    return decodeURIComponent(family);
                }
            } catch {
                const match = href.match(/family=([^&]+)/);
                if (match && match[1]) {
                    const family = match[1].split(':')[0].replace(/\+/g, ' ');
                    return decodeURIComponent(family);
                }
            }
        }
    } catch {
        return null;
    }

    return null;
};

const PageBuilderNew: React.FC = () => {
    const { theme } = useTheme();
    const [canvasItems, setCanvasItems] = useState<CanvasItem[]>([]);
    const [draggedElement, setDraggedElement] = useState<string | null>(null);
    const [selectedElement, setSelectedElement] = useState<string | null>(null);
    const [settingsPanel, setSettingsPanel] = useState<SettingsPanelConfig | null>(null);
    const [tempSettings, setTempSettings] = useState<TempSettings>({});
    const [pendingChanges, setPendingChanges] = useState<Map<string, TempSettings>>(new Map());
    const [canvasItemsSnapshot, setCanvasItemsSnapshot] = useState<CanvasItem[] | null>(null);
    const [elementSearchTerm, setElementSearchTerm] = useState('');
    const [debouncedElementSearchTerm, setDebouncedElementSearchTerm] = useState('');
    const [showPreview, setShowPreview] = useState(false);
    const [isPublishLoading, setIsPublishLoading] = useState(false);
    const [isDraftLoading, setIsDraftLoading] = useState(false);
    const [showWarningPopup, setShowWarningPopup] = useState(false);
    const navigationConfirmedRef = useRef(false);
    const pendingConfirmationActionRef = useRef<(() => void) | null>(null);
    const savedCanvasSnapshotRef = useRef('[]');
    const initialTempSettingsSnapshotRef = useRef('{}');
    const skipConfirmationRef = useRef(false);
    const copyIconResetTimeoutRef = useRef<number | null>(null);
    const [showCopiedTick, setShowCopiedTick] = useState(false);

    const { id } = useParams();
    const navigate = useNavigate();
    const showToast = useToast();
    const [publishPage, { isLoading: publishPageLoading }] = usePublishPageLayoutMutation();
    const [publishHeaderFooter, { isLoading: publishHeaderFooterLoading }] = usePublishHeaderFooterMutation();
    const { data: pageElementsData, isLoading: pageElementsLoading, isFetching: pageElementsFetching } = useGetPageElementsQuery(debouncedElementSearchTerm || undefined, {
        refetchOnMountOrArgChange: true,
    });
    const { data: pageLayoutData, isLoading: pageLayoutLoading, refetch: refetchLayout } = useGetPageLayoutQuery(Number(id), { refetchOnMountOrArgChange: true });
    const { data: menus, refetch: menuRefetch, isLoading: isLoadingMenus, isFetching: isFetchingMenus } = useGetMenusQuery("header,footer", { refetchOnMountOrArgChange: true });
    let { customCss = '', customJs = '', headerHtml = '' } = pageLayoutData?.website?.settings?.metaData?.customCodeSetting || {};
    const colorSettings = pageLayoutData?.website?.settings?.metaData?.colorSettings as
        { primaryColor?: string; textColor?: string; secondaryColor?: string } | undefined;
    const headerFontFamily = useMemo(() => getFontFamilyFromHeaderHtml(headerHtml), [headerHtml]);
    const basePrimaryFont = headerFontFamily || pageLayoutData?.website?.settings?.metaData?.font?.primaryFont || font.raleway;
    const primaryFont = /sans-serif/i.test(basePrimaryFont) ? basePrimaryFont : `${basePrimaryFont}, sans-serif`;
    const isPageLoading = pageElementsLoading || pageLayoutLoading || isLoadingMenus || isFetchingMenus;

    const serializeState = useCallback((value: unknown) => JSON.stringify(value ?? null), []);

    const hasDirtySettingsPanel = useCallback(() => {
        if (!settingsPanel) {
            return false;
        }

        return serializeState(tempSettings) !== initialTempSettingsSnapshotRef.current;
    }, [serializeState, settingsPanel, tempSettings]);

    const hasUnsavedChanges = useCallback(() => {
        return pendingChanges.size > 0 || serializeState(canvasItems) !== savedCanvasSnapshotRef.current || hasDirtySettingsPanel();
    }, [pendingChanges, canvasItems, serializeState, hasDirtySettingsPanel]);

    const requestConfirmation = useCallback((action: () => void) => {
        if (skipConfirmationRef.current) {
            action();
            return;
        }

        pendingConfirmationActionRef.current = action;
        setShowWarningPopup(true);
    }, []);

    const beginSaveBypass = useCallback(() => {
        skipConfirmationRef.current = true;
        pendingConfirmationActionRef.current = null;
        setShowWarningPopup(false);
        navigationConfirmedRef.current = true;
    }, []);

    const resetConfirmationBypass = useCallback(() => {
        skipConfirmationRef.current = false;
        if (hasUnsavedChanges()) {
            navigationConfirmedRef.current = false;
        }
    }, [hasUnsavedChanges]);

    const commitSettingsPanelChange = useCallback((nextPanel: SettingsPanelConfig | null, nextTempSettings: TempSettings = {}) => {
        if (nextPanel) {
            initialTempSettingsSnapshotRef.current = serializeState(nextTempSettings);
            setTempSettings(nextTempSettings);
            setSettingsPanel(nextPanel);
            return;
        }

        initialTempSettingsSnapshotRef.current = serializeState({});
        setTempSettings({});
        setSettingsPanel(null);
    }, [serializeState]);

    const handleSettingsPanelChange = useCallback((
        nextPanel: SettingsPanelConfig | null,
        nextTempSettings: TempSettings = {},
        options?: { skipPrompt?: boolean }
    ) => {
        const applyChange = () => {
            if (!options?.skipPrompt && settingsPanel?.type === 'section' && canvasItemsSnapshot) {
                setCanvasItems(canvasItemsSnapshot);
            }

            commitSettingsPanelChange(nextPanel, nextTempSettings);
        };

        if (!options?.skipPrompt && hasDirtySettingsPanel()) {
            requestConfirmation(applyChange);
            return;
        }

        applyChange();
    }, [settingsPanel, canvasItemsSnapshot, commitSettingsPanelChange, hasDirtySettingsPanel, requestConfirmation]);

    const handleOpenSettingsPanel = useCallback((config: SettingsPanelConfig, currentSettings: TempSettings = {}) => {
        handleSettingsPanelChange(config, currentSettings);
    }, [handleSettingsPanelChange]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setDebouncedElementSearchTerm(elementSearchTerm.trim());
        }, 400);

        return () => {
            window.clearTimeout(timer);
        };
    }, [elementSearchTerm]);

    useEffect(() => {
        if (hasUnsavedChanges()) {
            navigationConfirmedRef.current = false;
        }
    }, [canvasItems, pendingChanges, settingsPanel, tempSettings, hasUnsavedChanges]);

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasUnsavedChanges() && !navigationConfirmedRef.current) {
                e.preventDefault();
                e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [hasUnsavedChanges]);

    const useNavigationBlocker = (shouldBlock: boolean) => {
        const { navigator } = useContext(NavigationContext);

        useEffect(() => {
            if (!shouldBlock) {
                return;
            }

            const push = navigator.push;
            navigator.push = (...args: Parameters<typeof push>) => {
                const [to, state, options] = args;

                if (skipConfirmationRef.current) {
                    push(to, state, options);
                    return;
                }

                requestConfirmation(() => {
                    navigationConfirmedRef.current = true;
                    push(to, state, options);
                });

                return;
            };

            return () => {
                navigator.push = push;
            };
        }, [navigator, requestConfirmation, shouldBlock]);
    };

    useNavigationBlocker(hasUnsavedChanges() && !navigationConfirmedRef.current);

    // Snapshot canvasItems when section properties panel opens for Cancel restoration
    useEffect(() => {
        if (settingsPanel?.type === 'section') {
            // Save a deep copy of canvasItems when section panel opens
            setCanvasItemsSnapshot(JSON.parse(JSON.stringify(canvasItems)));
        } else if (settingsPanel === null) {
            // Clear snapshot when panel closes
            setCanvasItemsSnapshot(null);
        }
    }, [settingsPanel?.type, settingsPanel === null ? null : settingsPanel]);


    useEffect(() => {
        if (pageLayoutData?.page?.rows && !pageLayoutLoading) {
            const transformedCanvas: CanvasItem[] = [];
            const defaultButtonTextColor = getElementDefaultSettings('button', pageElementsData?.templates)?.buttonTextColor || '#D0E8FF';

            const normalizePublicDirectoryProperties = (settings: Record<string, any>) => {
                const publicDirectorySetting = settings.publicDirectorySetting || {};
                const queryPayload = settings.queryPayload
                    ?? publicDirectorySetting.queryPayload
                    ?? settings.publicDirectoryPayload;

                return {
                    ...settings,
                    scope: settings.scope ?? publicDirectorySetting.scope ?? '',
                    queryPayload,
                    publicDirectoryPayload: queryPayload,
                    publicDirectorySetting: {
                        name: publicDirectorySetting.name ?? 'public directory',
                        scope: publicDirectorySetting.scope ?? settings.scope ?? '',
                        queryPayload
                    }
                };
            };

            pageLayoutData?.page?.rows?.forEach((row: any, index: number) => {
                const columnType = row.column === 1 ? 'single_column' : 'multi_column';
                const sectionSettings = row.settings || {};
                const resolvedImageUrl = sectionSettings.imageSignedUrl || sectionSettings.imageUrl;
                const section: Section = {
                    id: `section-${row.id || Date.now()}-${index}`,
                    type: columnType,
                    properties: {
                        ...sectionSettings,
                        imagePreview: resolvedImageUrl
                    },
                    elements: [],
                    ...(columnType === 'multi_column' && {
                        columns: {
                            left: [],
                            right: []
                        }
                    })
                };

                // Transform elements
                row.elements?.forEach((el: any) => {
                    const elementConfig = el.settings?.config || {};
                    const hydratedElementConfig: any = { ...elementConfig };
                    const apiProvidedColorKeys: string[] = [];

                    if (el.accessKey === 'heading' && elementConfig.color !== undefined && elementConfig.color !== null && elementConfig.color !== '') {
                        apiProvidedColorKeys.push('color');
                    }

                    if (el.accessKey === 'paragraph' && elementConfig.paragraphColor !== undefined && elementConfig.paragraphColor !== null && elementConfig.paragraphColor !== '') {
                        apiProvidedColorKeys.push('paragraphColor');
                    }

                    if (el.accessKey === 'separator' && elementConfig.separatorColor !== undefined && elementConfig.separatorColor !== null && elementConfig.separatorColor !== '') {
                        apiProvidedColorKeys.push('separatorColor');
                    }

                    if (el.accessKey === 'button') {
                        if (elementConfig.buttonColor !== undefined && elementConfig.buttonColor !== null && elementConfig.buttonColor !== '') {
                            apiProvidedColorKeys.push('buttonColor');
                        }
                        if (elementConfig.buttonHoverColor !== undefined && elementConfig.buttonHoverColor !== null && elementConfig.buttonHoverColor !== '') {
                            apiProvidedColorKeys.push('buttonHoverColor');
                        }
                    }

                    // Apply website-level default colors only when color keys are absent in payload
                    if (el.accessKey === 'heading' && (hydratedElementConfig.color === undefined || hydratedElementConfig.color === null || hydratedElementConfig.color === '')) {
                        if (colorSettings?.primaryColor) {
                            hydratedElementConfig.color = colorSettings.primaryColor;
                        }
                    }

                    if (el.accessKey === 'paragraph' && (hydratedElementConfig.paragraphColor === undefined || hydratedElementConfig.paragraphColor === null || hydratedElementConfig.paragraphColor === '')) {
                        if (colorSettings?.textColor) {
                            hydratedElementConfig.paragraphColor = colorSettings.textColor;
                        }
                    }

                    if (el.accessKey === 'separator' && (hydratedElementConfig.separatorColor === undefined || hydratedElementConfig.separatorColor === null || hydratedElementConfig.separatorColor === '')) {
                        if (colorSettings?.secondaryColor) {
                            hydratedElementConfig.separatorColor = colorSettings.secondaryColor;
                        }
                    }

                    if (el.accessKey === 'button') {
                        if ((hydratedElementConfig.buttonColor === undefined || hydratedElementConfig.buttonColor === null || hydratedElementConfig.buttonColor === '') && colorSettings?.primaryColor) {
                            hydratedElementConfig.buttonColor = colorSettings.primaryColor;
                        }
                        if ((hydratedElementConfig.buttonHoverColor === undefined || hydratedElementConfig.buttonHoverColor === null || hydratedElementConfig.buttonHoverColor === '') && colorSettings?.primaryColor) {
                            hydratedElementConfig.buttonHoverColor = colorSettings.primaryColor;
                        }
                        if (hydratedElementConfig.buttonTextColor === undefined || hydratedElementConfig.buttonTextColor === null || hydratedElementConfig.buttonTextColor === '') {
                            hydratedElementConfig.buttonTextColor = defaultButtonTextColor;
                        }
                    }
                    // const resolvedElementImageUrl = elementConfig.imageSignedUrl || elementConfig.imageUrl;

                    // Elements can have image or video
                    let resolvedElementUrl = hydratedElementConfig.imageUrl;
                    if (hydratedElementConfig.mediaType === 'image' || hydratedElementConfig.mediaType === 'Image') {
                        resolvedElementUrl = hydratedElementConfig.imageSignedUrl || hydratedElementConfig.imageUrl;
                    } else if (hydratedElementConfig.mediaType === 'video' || hydratedElementConfig.mediaType === 'Video') {
                        resolvedElementUrl = hydratedElementConfig.videoSignedUrl || hydratedElementConfig.videoUrl;
                    }

                    const element: Element = {
                        id: `${el.accessKey}-${el.id || Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                        type: el.accessKey,
                        content: hydratedElementConfig.content || hydratedElementConfig.htmlContent || '',
                        // properties: elementConfig
                        properties: {
                            ...(el.accessKey === 'public_directory'
                                ? normalizePublicDirectoryProperties(hydratedElementConfig)
                                : hydratedElementConfig),
                            _apiColorKeys: apiProvidedColorKeys,
                            // imagePreview: resolvedElementImageUrl
                            imagePreview: hydratedElementConfig.mediaType === 'image' || hydratedElementConfig.mediaType === 'Image' ? resolvedElementUrl : undefined,
                            videoPreview: hydratedElementConfig.mediaType === 'video' || hydratedElementConfig.mediaType === 'Video' ? resolvedElementUrl : undefined
                        }
                    };

                    if (columnType === 'single_column') {
                        section.elements.push(element);
                    } else {
                        // Multi-column: distribute by belongsTo
                        if (el.belongsTo === 1) {
                            (section.columns as any).left.push(element);
                        } else if (el.belongsTo === 2) {
                            (section.columns as any).right.push(element);
                        }
                    }
                });

                transformedCanvas.push(section);
            });

            setCanvasItems(transformedCanvas);
            savedCanvasSnapshotRef.current = serializeState(transformedCanvas);
        } else if (!pageLayoutLoading) {
            savedCanvasSnapshotRef.current = serializeState([]);
        }
    }, [pageLayoutData, pageLayoutLoading, pageElementsData, colorSettings, serializeState]);

    // Dynamically map basic elements from API
    const basicElements = useMemo(() => {
        if (pageElementsData?.templates) {
            return mapTemplateToBasicElements(pageElementsData.templates);
        }
        return [];
    }, [pageElementsData]);

    // Static section elements
    const sections = [
        { type: 'single_column', label: 'Single Column', icon: <SingleColumnIcon /> },
        { type: 'multi_column', label: 'Multi Column', icon: <MultiColumnIcon /> }
    ];

    // Inject header HTML into document head (for meta tags, links, etc.)
    useEffect(() => {
        if (headerHtml && headerHtml.trim()) {
            try {
                // Create a temporary container to parse the HTML
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = headerHtml;

                // Get all elements and append them to head
                Array.from(tempDiv.children).forEach((element) => {
                    if (element.tagName.toLowerCase() === 'script') {
                        // Do not execute website-level scripts in the editor surface.
                        return;
                    }

                    const clonedElement = element.cloneNode(true) as HTMLElement;
                    clonedElement.setAttribute('data-custom-head', 'page-builder');
                    document.head.appendChild(clonedElement);
                });

                // Cleanup function
                return () => {
                    document.querySelectorAll('[data-custom-head="page-builder"]').forEach(el => el.remove());
                };
            } catch (error) {
                console.error('Error injecting header HTML:', error);
            }
        }
    }, [headerHtml]);

    // Inject custom CSS with high priority (after styled-components)
    useEffect(() => {
        if (customCss && customCss.trim()) {
            try {
                // Remove existing custom CSS first
                const existingStyle = document.querySelector('style[data-custom-css="page-builder"]');
                if (existingStyle) {
                    existingStyle.remove();
                }

                // Create a style element
                const style = document.createElement('style');
                style.setAttribute('type', 'text/css');
                style.setAttribute('data-custom-css', 'page-builder');
                style.textContent = customCss;

                // Append to head (after other styles for higher priority)
                document.head.appendChild(style);

                // Cleanup function
                return () => {
                    const styleToRemove = document.querySelector('style[data-custom-css="page-builder"]');
                    if (styleToRemove) {
                        styleToRemove.remove();
                    }
                };
            } catch (error) {
                console.error('❌ Error injecting custom CSS:', error);
            }
        }
    }, [customCss]);

    // Do not execute website-level custom JavaScript in the editor surface.
    useEffect(() => {
        const existingScript = document.querySelector('script[data-custom-js="page-builder"]');
        if (existingScript) {
            existingScript.remove();
        }

        return () => {
            const scriptToRemove = document.querySelector('script[data-custom-js="page-builder"]');
            if (scriptToRemove) {
                scriptToRemove.remove();
            }
        };
    }, [customJs]);

    const menuList = useMemo(() => {
        if (Array.isArray(menus)) {
            return menus;
        }
        return menus?.menus || [];
    }, [menus]);

    const headerMenuConfig = useMemo(() => {
        const headerMenu = menuList.find((menu: any) => menu.accessKey === 'header');
        const styleSettings = headerMenu?.config?.styleSettings;
        const styling = styleSettings?.styling;
        const menuDefaults: TempSettings = {};

        if (styleSettings?.layout?.type) {
            menuDefaults.headerAlignment = styleSettings.layout.type;
        }
        if (styling?.textColor !== undefined && styling?.textColor !== null) {
            menuDefaults.textColor = styling.textColor;
        }
        if (styling?.backgroundColor !== undefined && styling?.backgroundColor !== null) {
            menuDefaults.backgroundColor = styling.backgroundColor;
        }
        if (headerMenu?.config?.websiteLogo !== undefined && headerMenu?.config?.websiteLogo !== null) {
            menuDefaults.websiteLogo = headerMenu.config.websiteLogo;
        }

        return menuDefaults;
    }, [menuList]);

    const footerMenuConfig = useMemo(() => {
        const footerMenu = menuList.find((menu: any) => menu.accessKey === 'footer');
        const styleSettings = footerMenu?.config?.styleSettings;
        const contentSettings = footerMenu?.config?.html?.content;
        const socialMedia = styleSettings?.socialMedia;
        const menuDefaults: TempSettings = {};

        if (styleSettings?.textColor !== undefined && styleSettings?.textColor !== null) {
            menuDefaults.textColor = styleSettings.textColor;
        }
        if (styleSettings?.backgroundColor !== undefined && styleSettings?.backgroundColor !== null) {
            menuDefaults.backgroundColor = styleSettings.backgroundColor;
        }

        if (socialMedia) {
            if (socialMedia.facebook?.url !== undefined && socialMedia.facebook?.url !== null) {
                menuDefaults.facebookUrl = socialMedia.facebook.url;
            }
            if (socialMedia.linkedin?.url !== undefined && socialMedia.linkedin?.url !== null) {
                menuDefaults.linkedinUrl = socialMedia.linkedin.url;
            }
            if (socialMedia.youtube?.url !== undefined && socialMedia.youtube?.url !== null) {
                menuDefaults.youtubeUrl = socialMedia.youtube.url;
            }
            if (socialMedia.twitter?.url !== undefined && socialMedia.twitter?.url !== null) {
                menuDefaults.twitterUrl = socialMedia.twitter.url;
            }
        }

        if (contentSettings) {
            if (contentSettings.overview !== undefined && contentSettings.overview !== null) {
                menuDefaults.content = contentSettings.overview;
            }
            if (contentSettings.address !== undefined && contentSettings.address !== null) {
                menuDefaults.address = contentSettings.address;
            }
            if (contentSettings.copyrightText !== undefined && contentSettings.copyrightText !== null) {
                menuDefaults.copyrightText = contentSettings.copyrightText;
            }
            if (contentSettings.showPrivacyPolicy !== undefined && contentSettings.showPrivacyPolicy !== null) {
                menuDefaults.showPrivacyPolicy = contentSettings.showPrivacyPolicy;
            }
            if (contentSettings.showTermsAndConditions !== undefined && contentSettings.showTermsAndConditions !== null) {
                menuDefaults.showTermsAndConditions = contentSettings.showTermsAndConditions;
            }
        }

        if (footerMenu?.config?.websiteLogo !== undefined && footerMenu?.config?.websiteLogo !== null) {
            menuDefaults.websiteLogo = footerMenu.config.websiteLogo;
        }

        if (footerMenu?.config?.privacyPolicy !== undefined && footerMenu?.config?.privacyPolicy !== null) {
            menuDefaults.privacyPolicy = footerMenu.config.privacyPolicy;
        }
        if (footerMenu?.config?.termOfService !== undefined && footerMenu?.config?.termOfService !== null) {
            menuDefaults.termOfService = footerMenu.config.termOfService;
        }

        return menuDefaults;
    }, [menuList]);

    const headerDefaults = useMemo(() => {
        const defaultSettings = getElementDefaultSettings('header', pageElementsData?.templates);
        return {
            ...defaultSettings,
            ...headerMenuConfig
        };
    }, [headerMenuConfig, pageElementsData]);

    const footerDefaults = useMemo(() => {
        const defaultSettings = getElementDefaultSettings('footer', pageElementsData?.templates);
        const normalizedDefaults: any = { ...defaultSettings };

        if (!normalizedDefaults.content) {
            if (typeof normalizedDefaults.overview === 'string') {
                normalizedDefaults.content = normalizedDefaults.overview;
            } else if (typeof normalizedDefaults.htmlContent === 'string') {
                normalizedDefaults.content = normalizedDefaults.htmlContent;
            }
        }

        if (normalizedDefaults.socialMedia) {
            const socialMedia = normalizedDefaults.socialMedia;
            normalizedDefaults.facebookUrl = normalizedDefaults.facebookUrl || socialMedia.facebook?.url || '';
            normalizedDefaults.linkedinUrl = normalizedDefaults.linkedinUrl || socialMedia.linkedin?.url || '';
            normalizedDefaults.youtubeUrl = normalizedDefaults.youtubeUrl || socialMedia.youtube?.url || '';
            normalizedDefaults.twitterUrl = normalizedDefaults.twitterUrl || socialMedia.twitter?.url || '';
        }

        return {
            ...normalizedDefaults,
            ...footerMenuConfig
        };
    }, [footerMenuConfig, pageElementsData]);

    // Extract header and footer menu items
    const headerMenuItems = useMemo(() => {
        const headerMenu = menuList.find((menu: any) => menu.accessKey === 'header');
        return headerMenu?.pages || [];
    }, [menuList]);

    const footerMenuItems = useMemo(() => {
        const footerMenu = menuList.find((menu: any) => menu.accessKey === 'footer');
        return footerMenu?.pages || [];
    }, [menuList]);

    const handleOpenHeaderSettings = () => {
        const nextSettings = pendingChanges.get('page-header');
        const cleanedSettings = nextSettings ? { ...nextSettings } : {};
        if (cleanedSettings._meta) {
            delete cleanedSettings._meta;
        }
        const finalSettings = {
            ...headerDefaults,
            ...cleanedSettings
        };
        handleOpenSettingsPanel({ type: 'header', itemId: 'page-header' }, finalSettings);
    };

    const handleOpenFooterSettings = () => {
        const nextSettings = pendingChanges.get('page-footer');
        const cleanedSettings = nextSettings ? { ...nextSettings } : {};
        if (cleanedSettings._meta) {
            delete cleanedSettings._meta;
        }
        const finalSettings = {
            ...footerDefaults,
            ...cleanedSettings
        };
        handleOpenSettingsPanel({ type: 'footer', itemId: 'page-footer' }, finalSettings);
    };

    const applyPendingChangesToCanvas = useCallback((baseItems: CanvasItem[]) => {
        let items = [...baseItems];

        const buildPublicDirectoryProperties = (settings: Record<string, any>) => {
            const queryPayload = settings.queryPayload ?? settings.publicDirectoryPayload;
            return {
                scope: settings.scope,
                htmlConfig: settings.htmlConfig ?? {
                    tag: 'div',
                    content: '',
                    attributes: {
                        class: 'public_directory-element'
                    }
                },
                elementClass: settings.elementClass,
                queryPayload
            };
        };

        pendingChanges.forEach((settings) => {
            const meta = settings._meta as any;
            if (!meta) {
                return;
            }

            const cleanedSettings = { ...settings };
            delete cleanedSettings._meta;

            items = items.map((item: CanvasItem) => {
                if (meta.type === 'element' && item.id === meta.itemId && 'content' in item) {
                    const element = item as any;
                    const nextProperties = meta.itemType === 'public_directory'
                        ? buildPublicDirectoryProperties(cleanedSettings)
                        : { ...(element.properties || {}), ...cleanedSettings };
                    return {
                        ...element,
                        content: cleanedSettings.content || element.content,
                        properties: nextProperties
                    };
                }

                if (meta.type === 'section' && item.id === meta.itemId && 'type' in item) {
                    const section = item as any;
                    return {
                        ...section,
                        properties: { ...(section.properties || {}), ...cleanedSettings }
                    };
                }

                if (meta.type === 'column-element' && 'type' in item && item.id === meta.sectionId) {
                    const section = item as any;

                    if (section.type === 'single_column' || meta.column === 'single') {
                        return {
                            ...section,
                            elements: section.elements.map((el: any) =>
                                el.id === meta.itemId
                                    ? {
                                        ...el,
                                        content: cleanedSettings.content || el.content,
                                        properties: meta.itemType === 'public_directory'
                                            ? buildPublicDirectoryProperties(cleanedSettings)
                                            : { ...(el.properties || {}), ...cleanedSettings }
                                    }
                                    : el
                            )
                        };
                    }

                    if (section.type === 'multi_column' && section.columns && meta.column && meta.column !== 'single') {
                        return {
                            ...section,
                            columns: {
                                ...section.columns,
                                [meta.column]: section.columns[meta.column].map((el: any) =>
                                    el.id === meta.itemId
                                        ? {
                                            ...el,
                                            content: cleanedSettings.content || el.content,
                                            properties: meta.itemType === 'public_directory'
                                                ? buildPublicDirectoryProperties(cleanedSettings)
                                                : { ...(el.properties || {}), ...cleanedSettings }
                                        }
                                        : el
                                )
                            }
                        };
                    }
                }

                return item;
            });
        });

        return items;
    }, [pendingChanges]);

    // Handler to publish all pending changes at once
    // const handlePublish = () => {
    //     // if (pendingChanges.size === 0) {
    //     //     return;
    //     // }

    //     let updatedItems: CanvasItem[] = [];

    //     setCanvasItems((prevItems: CanvasItem[]) => {
    //         let items = [...prevItems];

    //         // Apply all pending changes
    //         pendingChanges.forEach((settings, changeKey) => {
    //             const meta = settings._meta as any;

    //             // Create a clean settings object without _meta
    //             const cleanedSettings = { ...settings };
    //             delete cleanedSettings._meta;

    //             items = items.map((item: CanvasItem) => {
    //                 // Handle standalone elements
    //                 if (meta.type === 'element' && item.id === meta.itemId && 'content' in item) {
    //                     const element = item as any;
    //                     return {
    //                         ...element,
    //                         content: cleanedSettings.content || element.content,
    //                         properties: { ...(element.properties || {}), ...cleanedSettings }
    //                     };
    //                 }

    //                 // Handle sections
    //                 if (meta.type === 'section' && item.id === meta.itemId && 'type' in item) {
    //                     const section = item as any;
    //                     return {
    //                         ...section,
    //                         properties: { ...(section.properties || {}), ...cleanedSettings }
    //                     };
    //                 }

    //                 // Handle column elements
    //                 if (meta.type === 'column-element' && 'type' in item && item.id === meta.sectionId) {
    //                     const section = item as any;

    //                     if (section.type === 'single-column' || meta.column === 'single') {
    //                         return {
    //                             ...section,
    //                             elements: section.elements.map((el: any) =>
    //                                 el.id === meta.itemId
    //                                     ? {
    //                                         ...el,
    //                                         content: cleanedSettings.content || el.content,
    //                                         properties: { ...(el.properties || {}), ...cleanedSettings }
    //                                     }
    //                                     : el
    //                             )
    //                         };
    //                     } else if (section.type === 'multi_column' && section.columns && meta.column && meta.column !== 'single') {
    //                         return {
    //                             ...section,
    //                             columns: {
    //                                 ...section.columns,
    //                                 [meta.column]: section.columns[meta.column].map((el: any) =>
    //                                     el.id === meta.itemId
    //                                         ? {
    //                                             ...el,
    //                                             content: cleanedSettings.content || el.content,
    //                                             properties: { ...(el.properties || {}), ...cleanedSettings }
    //                                         }
    //                                         : el
    //                                 )
    //                             }
    //                         };
    //                     }
    //                 }

    //                 return item;
    //             });
    //         });

    //         updatedItems = items;
    //         return items;
    //     });

    //     // Clear pending changes after publishing
    //     // setPendingChanges(new Map());

    //     // Generate and log the payload that would be sent to backend
    //     const headerSettings = pendingChanges.get('page-header');
    //     const footerSettings = pendingChanges.get('page-footer');

    //     const cleanHeaderSettings = headerSettings ? { ...headerSettings } : { ...headerDefaults };
    //     const cleanFooterSettings = footerSettings ? { ...footerSettings } : { ...footerDefaults };

    //     delete (cleanHeaderSettings as any)._meta;
    //     delete (cleanFooterSettings as any)._meta;

    //     const payload = generatePagePayload(
    //         'Landing Page',
    //         updatedItems,
    //         cleanHeaderSettings,
    //         cleanFooterSettings,
    //         pageElementsData
    //     );

    //     console.log(payload, 'payload');



    //     publishPage({ id: id, data: payload }).then((response) => {
    //         console.log('Publish response:', response);
    //         refetchLayout();
    //     });

    //     console.log('Page Payload Ready for Backend:', payload);

    //     // TODO: Add API call here to save to backend
    //     // const response = await publishPageChanges(payload);
    // };


    const handlePublish = async () => {
        beginSaveBypass();
        setIsPublishLoading(true);
        const updatedItems = applyPendingChangesToCanvas(canvasItems);
        setCanvasItems(updatedItems);

        // Generate and log the payload immediately with updatedItems
        const headerSettings = pendingChanges.get('page-header');
        const footerSettings = pendingChanges.get('page-footer');
        const cleanHeaderSettings = headerSettings ? { ...headerSettings } : { ...headerDefaults };
        const cleanFooterSettings = footerSettings ? { ...footerSettings } : { ...footerDefaults };

        delete (cleanHeaderSettings as any)._meta;
        delete (cleanFooterSettings as any)._meta;

        const payload = generatePagePayload(
            'Landing Page',
            updatedItems,
            cleanHeaderSettings,
            cleanFooterSettings,
            pageElementsData,
            colorSettings
        );

        // // Log and extract public directory configs from payload
        // const publicDirElements = updatedItems
        //     .flatMap(item => {
        //         if ('elements' in item) return item.elements || [];
        //         if ('columns' in item) {
        //             return Object.values(item.columns || {}).flatMap(col => col || []);
        //         }
        //         return [];
        //     })
        //     .filter((el: any) => el.type === 'public_directory');

        // console.log('🔵 PAGE PUBLISH PAYLOAD:', {
        //     totalElements: updatedItems.length,
        //     publicDirectoryElements: publicDirElements.map((el: any) => ({
        //         id: el.id,
        //         type: el.type,
        //         properties: el.properties
        //     })),
        //     fullPayload: payload
        // });

        try {
            const headerMenuPayload = menuList.find((menu: any) => menu.accessKey === 'header');
            const footerMenuPayload = menuList.find((menu: any) => menu.accessKey === 'footer');
            // ✅ DEEP CLONE to ensure properties are writable
            let updatedHeaderMenu = headerMenuPayload ? JSON.parse(JSON.stringify(headerMenuPayload)) : null;
            let updatedFooterMenu = footerMenuPayload ? JSON.parse(JSON.stringify(footerMenuPayload)) : null;

            // Remove websiteLogo from payloads - it should not be sent to the API
            if (updatedHeaderMenu?.config?.websiteLogo) {
                delete updatedHeaderMenu.config.websiteLogo;
            }
            if (updatedFooterMenu?.config?.websiteLogo) {
                delete updatedFooterMenu.config.websiteLogo;
            }

            // Remove pages from payloads
            if (updatedHeaderMenu?.pages) {
                delete updatedHeaderMenu.pages;
            }
            if (updatedFooterMenu?.pages) {
                delete updatedFooterMenu.pages;
            }

            if (updatedHeaderMenu && cleanHeaderSettings) {
                // Update header menu styling from cleanHeaderSettings
                updatedHeaderMenu.config = updatedHeaderMenu.config || {};
                updatedHeaderMenu.config.styleSettings = updatedHeaderMenu.config.styleSettings || {};
                updatedHeaderMenu.config.styleSettings.styling = {
                    textColor: cleanHeaderSettings.textColor,
                    backgroundColor: cleanHeaderSettings.backgroundColor
                };
                if (cleanHeaderSettings.headerAlignment) {
                    updatedHeaderMenu.config.styleSettings.layout = {
                        type: cleanHeaderSettings.headerAlignment
                    };
                }
            }

            if (updatedFooterMenu && cleanFooterSettings) {
                // Update footer menu styling from cleanFooterSettings
                updatedFooterMenu.config = updatedFooterMenu.config || {};
                updatedFooterMenu.config.styleSettings = updatedFooterMenu.config.styleSettings || {};
                updatedFooterMenu.config.styleSettings.textColor = cleanFooterSettings.textColor;
                updatedFooterMenu.config.styleSettings.backgroundColor = cleanFooterSettings.backgroundColor;

                // Update content settings (overview and address)
                updatedFooterMenu.config.html = updatedFooterMenu.config.html || {};
                updatedFooterMenu.config.html.content = updatedFooterMenu.config.html.content || {};
                if (cleanFooterSettings.content !== undefined) {
                    updatedFooterMenu.config.html.content.overview = cleanFooterSettings.content;
                }
                if (cleanFooterSettings.address !== undefined) {
                    updatedFooterMenu.config.html.content.address = cleanFooterSettings.address;
                }
                if (cleanFooterSettings.copyrightText !== undefined) {
                    updatedFooterMenu.config.html.content.copyrightText = cleanFooterSettings.copyrightText;
                }
                if (cleanFooterSettings.showPrivacyPolicy !== undefined) {
                    updatedFooterMenu.config.html.content.showPrivacyPolicy = cleanFooterSettings.showPrivacyPolicy;
                }
                if (cleanFooterSettings.showTermsAndConditions !== undefined) {
                    updatedFooterMenu.config.html.content.showTermsAndConditions = cleanFooterSettings.showTermsAndConditions;
                }

                // Update social media URLs if they exist in cleanFooterSettings
                if (cleanFooterSettings.facebookUrl || cleanFooterSettings.linkedinUrl || cleanFooterSettings.youtubeUrl || cleanFooterSettings.twitterUrl) {
                    updatedFooterMenu.config.styleSettings.socialMedia = {
                        facebook: { url: cleanFooterSettings.facebookUrl },
                        linkedin: { url: cleanFooterSettings.linkedinUrl },
                        youtube: { url: cleanFooterSettings.youtubeUrl },
                        twitter: { url: cleanFooterSettings.twitterUrl }
                    };
                }
            }

            // Check if either header or footer changed
            const headerChanged = pendingChanges.has('page-header');
            const footerChanged = pendingChanges.has('page-footer');
            const hasChanges = headerChanged || footerChanged;


            if (hasChanges) {
                // Send only the updated menus without pages data
                const menusPayload = [updatedHeaderMenu, updatedFooterMenu].filter(Boolean);

                try {
                    const menuResult = await publishHeaderFooter({ menus: menusPayload }).unwrap();
                } catch (menuError: any) {
                    console.error('❌ publishHeaderFooter failed:', menuError);
                    throw menuError;
                }
            } else {
                console.log('⚠️ No header or footer changes to publish');
            }

            const response = await publishPage({ id: id, data: payload, statusId: Number(38) }).unwrap();

            // Clear pending changes on success
            setPendingChanges(new Map());
            navigationConfirmedRef.current = true;

            // Navigate to home/builder
            navigate('/home/builder');
            showToast("Successfully published page!", "Page published successfully", "success");
        } catch (error: any) {
            resetConfirmationBypass();
            console.error('❌ Error publishing page:', error);
            console.error('❌ Full error object:', JSON.stringify(error, null, 2));
            console.error('❌ Error status:', error?.status);
            console.error('❌ Error data:', error?.data);
            console.error('❌ Error message:', error?.message);

            // Try to extract the actual error message
            let errorMessage = 'Failed to publish page. Please try again.';

            if (error?.data?.message) {
                errorMessage = error.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }

            console.error('🔴 Final error message:', errorMessage);
            showToast("Error publishing page", errorMessage, "error");
            setIsPublishLoading(false);
        }
        finally {
            setIsPublishLoading(false);
        }


    };

    const handleDraft = async () => {
        beginSaveBypass();
        setIsDraftLoading(true);
        const updatedItems = applyPendingChangesToCanvas(canvasItems);
        setCanvasItems(updatedItems);

        // Generate payload
        const headerSettings = pendingChanges.get('page-header');
        const footerSettings = pendingChanges.get('page-footer');

        const cleanHeaderSettings = headerSettings ? { ...headerSettings } : { ...headerDefaults };
        const cleanFooterSettings = footerSettings ? { ...footerSettings } : { ...footerDefaults };

        delete (cleanHeaderSettings as any)._meta;
        delete (cleanFooterSettings as any)._meta;

        const payload = generatePagePayload(
            'Landing Page',
            updatedItems,
            cleanHeaderSettings,
            cleanFooterSettings,
            pageElementsData,
            colorSettings
        );


        try {
            const headerMenuPayload = menuList.find((menu: any) => menu.accessKey === 'header');
            const footerMenuPayload = menuList.find((menu: any) => menu.accessKey === 'footer');

            // ✅ DEEP CLONE to ensure properties are writable
            let updatedHeaderMenu = headerMenuPayload ? JSON.parse(JSON.stringify(headerMenuPayload)) : null;
            let updatedFooterMenu = footerMenuPayload ? JSON.parse(JSON.stringify(footerMenuPayload)) : null;

            // Remove websiteLogo from payloads - it should not be sent to the API
            if (updatedHeaderMenu?.config?.websiteLogo) {
                delete updatedHeaderMenu.config.websiteLogo;
            }
            if (updatedFooterMenu?.config?.websiteLogo) {
                delete updatedFooterMenu.config.websiteLogo;
            }

            // Remove pages from payloads
            if (updatedHeaderMenu?.pages) {
                delete updatedHeaderMenu.pages;
            }
            if (updatedFooterMenu?.pages) {
                delete updatedFooterMenu.pages;
            }

            if (updatedHeaderMenu && cleanHeaderSettings) {
                // Update header menu styling from cleanHeaderSettings
                updatedHeaderMenu.config = updatedHeaderMenu.config || {};
                updatedHeaderMenu.config.styleSettings = updatedHeaderMenu.config.styleSettings || {};
                updatedHeaderMenu.config.styleSettings.styling = {
                    textColor: cleanHeaderSettings.textColor,
                    backgroundColor: cleanHeaderSettings.backgroundColor
                };
                if (cleanHeaderSettings.headerAlignment) {
                    updatedHeaderMenu.config.styleSettings.layout = {
                        type: cleanHeaderSettings.headerAlignment
                    };
                }
            }

            if (updatedFooterMenu && cleanFooterSettings) {
                // Update footer menu styling from cleanFooterSettings
                updatedFooterMenu.config = updatedFooterMenu.config || {};
                updatedFooterMenu.config.styleSettings = updatedFooterMenu.config.styleSettings || {};
                updatedFooterMenu.config.styleSettings.textColor = cleanFooterSettings.textColor;
                updatedFooterMenu.config.styleSettings.backgroundColor = cleanFooterSettings.backgroundColor;

                // Update content settings (overview and address)
                updatedFooterMenu.config.html = updatedFooterMenu.config.html || {};
                updatedFooterMenu.config.html.content = updatedFooterMenu.config.html.content || {};
                if (cleanFooterSettings.content !== undefined) {
                    updatedFooterMenu.config.html.content.overview = cleanFooterSettings.content;
                }
                if (cleanFooterSettings.address !== undefined) {
                    updatedFooterMenu.config.html.content.address = cleanFooterSettings.address;
                }
                if (cleanFooterSettings.copyrightText !== undefined) {
                    updatedFooterMenu.config.html.content.copyrightText = cleanFooterSettings.copyrightText;
                }
                if (cleanFooterSettings.showPrivacyPolicy !== undefined) {
                    updatedFooterMenu.config.html.content.showPrivacyPolicy = cleanFooterSettings.showPrivacyPolicy;
                }
                if (cleanFooterSettings.showTermsAndConditions !== undefined) {
                    updatedFooterMenu.config.html.content.showTermsAndConditions = cleanFooterSettings.showTermsAndConditions;
                }

                // Update social media URLs if they exist in cleanFooterSettings
                if (cleanFooterSettings.facebookUrl || cleanFooterSettings.linkedinUrl || cleanFooterSettings.youtubeUrl || cleanFooterSettings.twitterUrl) {
                    updatedFooterMenu.config.styleSettings.socialMedia = {
                        facebook: { url: cleanFooterSettings.facebookUrl },
                        linkedin: { url: cleanFooterSettings.linkedinUrl },
                        youtube: { url: cleanFooterSettings.youtubeUrl },
                        twitter: { url: cleanFooterSettings.twitterUrl }
                    };
                }
            }

            // Check if either header or footer changed
            const headerChanged = pendingChanges.has('page-header');
            const footerChanged = pendingChanges.has('page-footer');
            const hasChanges = headerChanged || footerChanged;

            if (hasChanges) {
                // Send only the updated menus without pages data
                const menusPayload = [updatedHeaderMenu, updatedFooterMenu].filter(Boolean);

                try {
                    const menuResult = await publishHeaderFooter({ menus: menusPayload }).unwrap();
                } catch (menuError: any) {
                    console.error('❌ publishHeaderFooter (DRAFT) failed:', menuError);
                    throw menuError;
                }
            }

            const response = await publishPage({ id: id, data: payload, statusId: 6 }).unwrap();

            showToast("Success", "Page saved as draft successfully!", 'success');
            setPendingChanges(new Map());
            navigationConfirmedRef.current = true;
            navigate('/home/builder');
        } catch (error: any) {
            resetConfirmationBypass();

            let errorMessage = 'Failed to save draft. Please try again.';

            if (error?.data?.message) {
                errorMessage = error.data.message;
            } else if (error?.message) {
                errorMessage = error.message;
            } else if (typeof error === 'string') {
                errorMessage = error;
            }

            showToast("Error", errorMessage, 'error');
            setIsDraftLoading(false);
        } finally {
            setIsDraftLoading(false);
        }
    };

    useEffect(() => {
        return () => {
            if (copyIconResetTimeoutRef.current) {
                window.clearTimeout(copyIconResetTimeoutRef.current);
            }
        };
    }, []);

    const builderScrollRef = React.useRef<HTMLDivElement | null>(null);

    const handleCopyPageUrl = () => {
        copyToClipboard(`https://${pageLayoutData?.website?.cloudflareWebDomain}/?page=${pageLayoutData?.page?.url}`);
        setShowCopiedTick(true);

        if (copyIconResetTimeoutRef.current) {
            window.clearTimeout(copyIconResetTimeoutRef.current);
        }

        copyIconResetTimeoutRef.current = window.setTimeout(() => {
            setShowCopiedTick(false);
            copyIconResetTimeoutRef.current = null;
        }, 500);
    };

    // Handler to discard all pending changes
    const handleDiscardChanges = () => {
        if (window.confirm(`Are you sure you want to discard ${pendingChanges.size} unsaved change${pendingChanges.size > 1 ? 's' : ''}?`)) {
            setPendingChanges(new Map());
        }
    };

    return (
        <div>
            <Container theme={theme} className='rounded-0 pb-4' style={{ borderBottom: `1px solid ${theme.CorporateTheme.colors.border}` }}>
                <div className="d-flex justify-content-between mt-0">
                    <div className="d-flex align-items-center gap-2 justify-content-between w-100">
                        <TitleGroup>
                            {isPageLoading ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <SkeletonCell height="24px" width="240px" />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <SkeletonCell height="16px" width="360px" />
                                        <SkeletonCell height="16px" width="16px" rounded />
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <TitleWrapper>
                                        <Title theme={theme}>{pageLayoutData?.page?.name}</Title>
                                    </TitleWrapper>
                                    {pageLayoutData?.page?.statusId === Status?.Draft ? (
                                        <StyledText className='mt-2' fontSize="14px" fontWeight={300} fontFamily={font.lato} textColor={theme.CorporateTheme.colors.textSecondary}>
                                            Configure, design and manage your page here
                                        </StyledText>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <StyledText className='mt-2' fontSize="14px" fontWeight={300} fontFamily={font.lato} textColor={theme.CorporateTheme.colors.textSecondary}>
                                                {`https://${pageLayoutData?.website?.cloudflareWebDomain}/?page=${pageLayoutData?.page?.url}`}
                                            </StyledText>
                                            <div onClick={handleCopyPageUrl} style={{ cursor: 'pointer' }}>
                                                {showCopiedTick ? (
                                                    <RoundedGreenTick color={theme.CorporateTheme.colors.green} size={16} />
                                                ) : (
                                                    <NewCopyIcon color={theme.CorporateTheme.colors.gunmetal} size={16} />
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </TitleGroup>
                        {pageLayoutData?.page?.isStandard || pageLayoutData?.page?.statusId === Status?.Published || isPageLoading ? null : <StyledToolTip placement="bottom" title="Save as Draft">
                            <span
                                className="me-2"
                                onClick={handleDraft}

                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    cursor: isPublishLoading || isDraftLoading ? 'not-allowed' : 'pointer',
                                    opacity: isPublishLoading || isDraftLoading ? 0.6 : 1,
                                }}
                            >
                                {isDraftLoading ? <LoadingSpinner color={theme.CorporateTheme.colors.headerColor} size='20px' /> : <FormDraftIcon color={theme.CorporateTheme.colors.gunmetal} />}
                            </span>
                        </StyledToolTip>}

                        {isPageLoading ? <SkeletonCell height="42px" width="117px" /> : <Button
                            theme={theme}
                            type="button"
                            $variant="customButton"
                            className="border"
                            btnBg={theme.CorporateTheme.colors.headerBG}
                            onClick={() => setShowPreview(true)}
                            disabled={isPublishLoading || isDraftLoading}
                        >
                            <ViewIcon color={theme.CorporateTheme.colors.textSecondary} />
                            Preview
                        </Button>}

                        {/* {pendingChanges.size > 0 && (
                            <Button
                                theme={theme}
                                type="button"
                                $variant="customButton"
                                className="border"
                                btnBg={theme.CorporateTheme.colors.headerBG}
                                onClick={handleDiscardChanges}
                                style={{ color: '#dc3545' }}
                            >
                                Discard Changes
                            </Button>
                        )} */}

                        {isPageLoading ? <SkeletonCell height="42px" width="117px" /> : <Button
                            theme={theme}
                            type="submit"
                            $variant="add"
                            onClick={handlePublish}
                            disabled={isPublishLoading || isDraftLoading}
                        >
                            {isPublishLoading ? <LoadingSpinner color={theme.CorporateTheme.colors.headerColor} size='20px' /> : <FormsIcon color={theme.CorporateTheme.colors.background} />}
                            Publish
                        </Button>}
                    </div>
                </div>

            </Container>
            <AppContainer>
                <Sidebar
                    basicElements={basicElements}
                    sections={sections}
                    draggedElement={draggedElement}
                    setDraggedElement={setDraggedElement}
                    isLoading={pageElementsLoading || pageElementsFetching}
                    searchTerm={elementSearchTerm}
                    onSearchChange={setElementSearchTerm}
                />

                <div ref={builderScrollRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 30, background: theme.CorporateTheme.colors.builderBg, height: `calc(100vh - 149px)`, overflowY: 'auto' }}>
                    <div style={{ background: theme.CorporateTheme.colors.background, borderRadius: 8 }}>
                        {isPageLoading ? (
                            <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 24 }}>
                                <div style={{ padding: 16, borderRadius: 8, background: theme.CorporateTheme.colors.background }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <SkeletonCell height="40px" width="40px" rounded />
                                            <SkeletonCell height="18px" width="140px" />
                                        </div>
                                        <div style={{ display: 'flex', gap: 10 }}>
                                            <SkeletonCell height="18px" width="70px" />
                                            <SkeletonCell height="18px" width="70px" />
                                            <SkeletonCell height="18px" width="70px" />
                                        </div>
                                    </div>
                                </div>
                                <div style={{ padding: 16, borderRadius: 8, background: theme.CorporateTheme.colors.background }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        <SkeletonCell height="140px" width="100%" />
                                        <SkeletonCell height="140px" width="100%" />
                                        <SkeletonCell height="140px" width="100%" />
                                    </div>
                                </div>
                                <div style={{ padding: 16, borderRadius: 8, background: theme.CorporateTheme.colors.background }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 16 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <SkeletonCell height="20px" width="140px" />
                                            <SkeletonCell height="14px" width="90%" />
                                            <SkeletonCell height="14px" width="80%" />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <SkeletonCell height="16px" width="60%" />
                                            <SkeletonCell height="16px" width="70%" />
                                            <SkeletonCell height="16px" width="50%" />
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                            <SkeletonCell height="16px" width="70%" />
                                            <SkeletonCell height="16px" width="50%" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <>
                                <Header
                                    onOpenSettings={handleOpenHeaderSettings}
                                    tempSettings={tempSettings}
                                    defaultSettings={headerDefaults}
                                    pendingChanges={pendingChanges}
                                    isEditing={settingsPanel?.type === 'header'}
                                    menuItems={headerMenuItems}
                                    primaryFont={primaryFont}
                                />
                                <Canvas
                                    canvasItems={canvasItems}
                                    setCanvasItems={setCanvasItems}
                                    draggedElement={draggedElement}
                                    selectedElement={selectedElement}
                                    setSelectedElement={setSelectedElement}
                                    setDraggedElement={setDraggedElement}
                                    openSettingsPanel={handleOpenSettingsPanel}
                                    tempSettings={tempSettings}
                                    pendingChanges={pendingChanges}
                                    setPendingChanges={setPendingChanges}
                                    scrollContainerRef={builderScrollRef}
                                    pageElementsData={pageElementsData}
                                    primaryFont={primaryFont}
                                />
                                <Footer
                                    onOpenSettings={handleOpenFooterSettings}
                                    tempSettings={tempSettings}
                                    defaultSettings={footerDefaults}
                                    pendingChanges={pendingChanges}
                                    isEditing={settingsPanel?.type === 'footer'}
                                    menuItems={footerMenuItems}
                                    primaryFont={primaryFont}
                                />
                            </>
                        )}
                    </div>
                </div>
            </AppContainer>

            <SettingsPanel
                settingsPanel={settingsPanel}
                setSettingsPanel={handleSettingsPanelChange}
                tempSettings={tempSettings}
                setTempSettings={setTempSettings}
                canvasItems={canvasItems}
                setCanvasItems={setCanvasItems}
                pendingChanges={pendingChanges}
                setPendingChanges={setPendingChanges}
                canvasItemsSnapshot={canvasItemsSnapshot}
            />

            <PreviewModal
                isOpen={showPreview}
                onClose={() => setShowPreview(false)}
                pageName={pageLayoutData?.page?.name}
                canvasItems={canvasItems}
                headerSettings={settingsPanel?.type === 'header' ? tempSettings : {}}
                footerSettings={settingsPanel?.type === 'footer' ? tempSettings : {}}
                pendingChanges={pendingChanges}
                defaultHeaderSettings={headerDefaults}
                defaultFooterSettings={footerDefaults}
                headerMenuItems={headerMenuItems}
                footerMenuItems={footerMenuItems}
                primaryFont={primaryFont}
                customCss={customCss}
                customJs={customJs}
            />

            {showWarningPopup && (
                <ConfirmationPrompt
                    title="Cancel Editing?"
                    subTitle="You might have unsaved changes. Are you sure you want to leave without saving?"
                    onConfirm={() => {
                        setShowWarningPopup(false);
                        if (pendingConfirmationActionRef.current) {
                            pendingConfirmationActionRef.current();
                            pendingConfirmationActionRef.current = null;
                        }
                    }}
                    onCancel={() => {
                        pendingConfirmationActionRef.current = null;
                        setShowWarningPopup(false);
                    }}
                />
            )}
        </div>
    );
};

export default PageBuilderNew;